<template>
  <RevStepper
    v-if="activeStep"
    :active-step="activeStep.name"
    :alternative-text-back="i18n(translations.previousPage)"
    :alternative-text-close="i18n(translations.stepperClose)"
    :alternative-text-completed="i18n(translations.stepperCompleted)"
    :alternative-text-current="i18n(translations.stepperCurrent)"
    has-back
    :steps
    @back="handleStepBack"
  />
</template>

<script setup lang="ts">
import { navigateTo, useHead, useRoute } from '#imports'
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevStepper } from '@ds/components/Stepper'

import BouyguesStepperLogo from '~/components/BouyguesStepperLogo/BouyguesStepperLogo.vue'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { useDiscountStore } from '~/scopes/checkout/stores/discountStore'

import {
  CHECKOUT_ACTIVE_STEPS,
  CHECKOUT_PREVIOUS_ROUTES,
  CHECKOUT_PREVIOUS_ROUTES_SINGLE_PAGE_ADDRESSES,
} from './Stepper.constants'
import translations from './Stepper.translations'

const props = defineProps<{
  hasBouyguesSteps: boolean
}>()

const i18n = useI18n()
const route = useRoute()
const discountStore = useDiscountStore()
const experiments = useExperiments()

const bouyguesSteps = [
  {
    name: 'bouygues',
    label: i18n(translations.bouyguesStep),
    suffix: BouyguesStepperLogo,
  },
]

const isSinglePageAddressesEnabled = computed(() => {
  return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
})

const steps = computed(() => {
  return [
    {
      name: 'account',
      label: i18n(translations.accountStep),
    },
    isSinglePageAddressesEnabled.value
      ? {
          name: 'addresses',
          label: i18n(translations.addressesStep),
        }
      : {
          name: 'shipping',
          label: i18n(translations.shippingStep),
        },
    ...insertIf(props.hasBouyguesSteps, bouyguesSteps),
    {
      name: 'payment',
      label: i18n(translations.paymentStep),
    },
    {
      name: 'confirmation',
      label: i18n(translations.confirmationStep),
    },
  ]
})

const activeStep = computed(() =>
  steps.value.find(
    (step) => step.name === CHECKOUT_ACTIVE_STEPS[route.name as string],
  ),
)

useHead({
  title: computed(
    () =>
      `${activeStep.value ? `${activeStep.value.label} | ` : ''}${i18n(translations.pageTitle)}`,
  ),
})

const previousRoutes = computed(() => {
  return isSinglePageAddressesEnabled.value
    ? CHECKOUT_PREVIOUS_ROUTES_SINGLE_PAGE_ADDRESSES
    : CHECKOUT_PREVIOUS_ROUTES
})

const previousRouteName = computed(() => {
  if (!route.name) {
    return null
  }

  if (props.hasBouyguesSteps) {
    return {
      ...previousRoutes.value,
      [CHECKOUT.PAYMENT]: CHECKOUT.SERVICES.BOUYGUES.PHONELINE_SETUP,
    }[route.name as string]
  }

  return previousRoutes.value[route.name as string]
})

const handleStepBack = async () => {
  // TODO: investigate why we reset the discount when clicking on the back button.
  // If it's to avoid having the discount applied in previous steps, we should also
  // remove it when using the brower's back button.
  if (route.name === CHECKOUT.PAYMENT) {
    discountStore.setPaymentMethod(null)
  }

  if (previousRouteName.value) {
    await navigateTo({ name: previousRouteName.value })
  }
}
</script>
